import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getSpendByCountry } from 'utils/apiRequests';
import {
	ColorSymbol,
	ColorWrapper,
	TooltipGrid,
	TooltipRowTitle,
	TooltipRowValue,
	TooltipWrapper,
} from './DashboardChart.styles';
import {
	chartheight,
	FONTSTYLE,
} from 'CategoryManagement/Dashboard/ChartsList/chartUtils';
import { renderToString } from 'react-dom/server';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';

const SpendByCountry = createContext();

const color = [
	'#032E45',
	'#012935',
	'#00212E',
	'#001A27',
	'#00131F',
	'#000C18',
	'#000511',
	'#00020A',
	'#000003',
	'#000000',
];

export const SpendByCountryProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [options, setOptions] = useState({
		colorAxis: {
			minColor: '#FFFFFF',
			maxColor: '#032E45',
		},
		chart: {
			type: 'treemap',
			height: chartheight,
		},
		title: { text: '' },
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		legend: {
			symbolRadius: 1,
			symbolHeight: 16,
			useHTML: true,
			itemMarginBottom: 12,
			formatter: function() {
				return formatSpendWithDecimalAndSymbol(
					this.value,
					currencySymbol,
					hideDecimals,
					0
				).toUpperCase();
			},
			itemStyle: {
				...FONTSTYLE,
				textOverflow: 'ellipsis',
			},
		},
		tooltip: {
			enabled: true,
			shared: true,
			distance: 56,
			useHTML: true,
			backgroundColor: 'rgba(255,255,255, 0.9)',
			borderWidth: 0,
			outside: true,
			borderRadius: 2,
		},
		// series: [],
		colors: color,
	});

	const { currentSelectCategory, dateRangeData, isFetching } = useContext(
		CategoryManagementContext
	);
	const { loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
					};
					getSpendByCountry(payload)
						.then(response => {
							if (response.length > 0) {
								setData(response);
								const [yValue, xValue] = Object.keys(response[0]);
								const seriesData = response
									.sort((a, b) => b[xValue] - a[xValue])
									.slice(0, 10)
									.map(category => {
										return {
											name: category[yValue],
											value: category[xValue],
											colorValue: category[xValue],
										};
									});

								const seriesResult = [
									{
										layoutAlgorithm: 'squarified',
										data: seriesData,
									},
								];

								setOptions(prevState => ({
									...prevState,
									series: seriesResult,
									tooltip: {
										...prevState.tooltip,
										formatter: function() {
											const { points } = this;
											return response.length > 1
												? renderToString(
														<TooltipWrapper>
															<TooltipGrid>
																<React.Fragment key={this.point?.name}>
																	<ColorWrapper>
																		<ColorSymbol color={this.color} />
																	</ColorWrapper>
																	<TooltipRowTitle>
																		{this.point?.name}
																	</TooltipRowTitle>
																	<TooltipRowValue>
																		{formatSpendWithDecimalAndSymbol(
																			this.point.value,
																			currencySymbol,
																			hideDecimals,
																			2
																		).toUpperCase()}
																	</TooltipRowValue>
																</React.Fragment>
															</TooltipGrid>
														</TooltipWrapper>
												  )
												: renderToString(
														<TooltipWrapper>
															<TooltipGrid>
																<ColorWrapper>
																	<ColorSymbol color={points[0].color} />
																</ColorWrapper>
																<TooltipRowValue>
																	{formatSpendWithDecimalAndSymbol(
																		points[0].y,
																		currencySymbol,
																		hideDecimals,
																		2
																	).toUpperCase()}
																</TooltipRowValue>
															</TooltipGrid>
														</TooltipWrapper>
												  );
										},
									},
								}));
								setIsLoading(false);
							} else {
								setIsLoading(false);
							}
						})
						.catch(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[dateRangeData, categoryName, isSettingsFetching, isFetching]
	);

	return (
		<SpendByCountry.Provider value={{ isLoading, data, options }}>
			{children}
		</SpendByCountry.Provider>
	);
};

export const useSpendByCountryContext = () => {
	return useContext(SpendByCountry);
};
